import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetStaticPaths, GetStaticPropsContext } from 'next';
import styled from 'styled-components';
import { useCallback } from 'react';
import { StaticPagesUtility } from '../../../new-lib/utilities/static-pages.utility';
import CityscapeOffersContainer from '../../../features/offers/components/CityscapeOffersContainer';
import { getFilterOptions, getFooterLinks } from '../../../new-lib/apis';
import { wrapper } from '../../../redux/store';
import {
  setExperimentVariant,
  setIsMobile
} from '../../../redux/actions/appActions';
import { ConfigsApis } from '../../../new-lib/apis/configs.apis';

interface ChildProps {
  configVariables: { configKey: string; configValue: any }[];
}

const CityScape = ({ configVariables }: ChildProps) => {
  const { t } = useTranslation('cityscape');

  const cityscapeState = (): string => {
    return configVariables.find(
      (item: any) => item.configKey == 'cityscape_offers'
    )?.configValue;
  };

  const getOffersStartDate = useCallback(() => {
    return (
      configVariables.find(
        (item: any) => item.configKey === 'nawy_exclusive_start_date'
      )?.configValue ?? ''
    );
  }, [configVariables]);

  const getPageView = () => {
    switch (cityscapeState()) {
      case 'on-has-offers':
        return (
          <CityscapeOffersContainer offersStartDate={getOffersStartDate()} />
        );
      case 'off':
      default:
        return (
          <ComingSoonContainer>
            <div className="info">
              <h1 className="headline-1">
                {t('cityscape')}
                <span>2023</span>
              </h1>
              <h2>{t('coming_soon')}</h2>
            </div>
          </ComingSoonContainer>
        );
    }
  };

  return <>{getPageView()}</>;
};

const ComingSoonContainer = styled.div`
  display: flex;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  .info {
    align-self: center;
  }

  h1 {
    font-size: 42px;

    span {
      color: #f5a450;
      padding-inline-start: 15px;
    }
  }
`;

export const getStaticProps = wrapper.getStaticProps(
  store =>
    async ({ params, locale }: GetStaticPropsContext) => {
      const [footerLinks, configVariables, filterOptions] = await Promise.all([
        getFooterLinks(locale, 'cityscape'),
        ConfigsApis.getConfigs(locale),
        getFilterOptions(locale)
      ]);

      store.dispatch(setIsMobile(params?.device === 'mobile'));
      store.dispatch(setExperimentVariant(params?.variant as string));

      return {
        revalidate: 60 * 5,
        props: {
          ...(await serverSideTranslations(locale as string, [
            'cityscape',
            'common',
            'contact'
          ])),
          filterOptions: JSON.stringify(filterOptions) || '',
          configVariables,
          layout: {
            url: `${process.env.NEXT_PUBLIC_HOST}/cityscape`,
            footerLinks
          }
        }
      };
    }
);

export const getStaticPaths = (async () => {
  return { paths: StaticPagesUtility.get(), fallback: false };
}) satisfies GetStaticPaths;

export default CityScape;
